import mammoth from "mammoth";

// mammoth converts the document to simple HTML with embedded images as
// data URIs; we walk the blocks in order and turn each into a unit.

const decodeEntities = (text) =>
  text
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(code))
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&");

const stripTags = (html) =>
  decodeEntities(
    html
      .replace(/<img[\s\S]*?>/g, " ")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim()
  );

/**
 * Parse a .docx into ordered units (one per paragraph block).
 * @param {File|Blob} file
 * @param {(name: string, blob: Blob) => Promise<string>} createImage
 * @returns {Promise<{kind: "docx", units: Array<{index: number, label: string, text: string, imageIds: string[]}>}>}
 */
export async function parseDocx(file, createImage) {
  const arrayBuffer = await file.arrayBuffer();
  // mammoth's two builds disagree on the input shape: the node build (jest,
  // via package.json "main") reads options.buffer, while the browser build
  // (webpack swaps in browser/unzip.js via the "browser" field) reads ONLY
  // options.arrayBuffer. Detecting the environment via `typeof Buffer` is
  // wrong in webpack — it polyfills Buffer, so the browser got {buffer} and
  // mammoth rejected it with "Could not find file in options". Hand over
  // both: arrayBuffer always, buffer as a lazy getter so only the build
  // that actually reads it pays for the copy.
  const input = {
    arrayBuffer,
    get buffer() {
      return Buffer.from(arrayBuffer);
    }
  };
  const { value: html } = await mammoth.convertToHtml(input);

  const units = [];
  // Mammoth renders auto-numbered/bulleted lists as <ul>/<ol><li> WITHOUT a
  // <p> inside each item, so walking <p> alone silently drops every list
  // item — and in MCQ banks the stems and options usually live in exactly
  // those lists. Walk both block kinds in document order.
  const blockRe = /<p\b[^>]*>([\s\S]*?)<\/p>|<li>([\s\S]*?)<\/li>/g;
  const imageRe = /<img[^>]*\bsrc="([^"]+)"/g;
  let imageCounter = 0;
  let match;

  while ((match = blockRe.exec(html))) {
    const isListItem = match[2] !== undefined;
    const blockHtml = isListItem ? match[2] : match[1];
    const text = stripTags(blockHtml);

    const imageIds = [];
    let imgMatch;
    imageRe.lastIndex = 0;
    while ((imgMatch = imageRe.exec(blockHtml))) {
      const dataUri = imgMatch[1];
      if (!dataUri.startsWith("data:")) continue;
      const blob = await (await fetch(dataUri)).blob();
      imageIds.push(
        await createImage(`doc_image_${++imageCounter}`, blob)
      );
    }

    if (!text && !imageIds.length) continue;

    units.push({
      index: units.length,
      label: `${isListItem ? "list item" : "paragraph"} ${
        units.length + 1
      }`,
      text,
      imageIds
    });
  }

  if (!units.length) {
    throw new Error("No readable content found in the document.");
  }

  return { kind: "docx", units };
}
